import { haversineMeters } from "./stage-connectors";
import type { MeasuredLine } from "../utils/trailLine";

/**
 * Where an off-trail drop rejoins the locked trail. The straight-line nearest
 * point is often the wrong one — across a river, a fence or a cliff there's no
 * way between the two — so several spaced-out points along the trail near the
 * drop are tried, and whichever the routing engine can actually reach by the
 * shortest path wins.
 */

/** How many rejoin points are routed per drop. */
export const GATE_CANDIDATE_COUNT = 5;
/**
 * Candidates are at least this far apart along the trail, so they're real
 * alternatives — or further, for a drop well off the trail, where the way
 * back is more likely to come out some distance along it.
 */
export const GATE_CANDIDATE_SPACING_M = 150;
const GATE_CANDIDATE_SPACING_FACTOR = 0.75;
/** A candidate further than this multiple of the nearest one (plus the slack) isn't worth routing. */
const GATE_CANDIDATE_REACH_FACTOR = 3;
const GATE_CANDIDATE_REACH_SLACK_M = 1000;

/**
 * Distances along the trail of the points worth trying as rejoin points for
 * `target`, nearest (as the crow flies) first, within [minDist, maxDist].
 */
export function candidateGateDistances(
  trail: MeasuredLine,
  target: number[],
  minDist: number,
  maxDist: number,
  count: number = GATE_CANDIDATE_COUNT,
  spacingM: number = GATE_CANDIDATE_SPACING_M
): number[] {
  const inRange = trail.coordinates
    .map((coord, i) => ({ distanceM: trail.cumDist[i], crowM: haversineMeters(target, coord) }))
    .filter(c => c.distanceM >= minDist && c.distanceM <= maxDist)
    .sort((a, b) => a.crowM - b.crowM);
  if (inRange.length === 0) return [];

  const reach = inRange[0].crowM * GATE_CANDIDATE_REACH_FACTOR + GATE_CANDIDATE_REACH_SLACK_M;
  const spacing = Math.max(spacingM, inRange[0].crowM * GATE_CANDIDATE_SPACING_FACTOR);
  const picked: number[] = [];
  for (const candidate of inRange) {
    if (picked.length >= count || candidate.crowM > reach) break;
    if (picked.every(d => Math.abs(d - candidate.distanceM) >= spacing)) picked.push(candidate.distanceM);
  }
  return picked;
}

/** The candidate with the shortest routed path; null when none could be routed. */
export function shortestRoutedCandidate<T extends { routedM: number | null }>(candidates: T[]): T | null {
  let best: T | null = null;
  for (const candidate of candidates) {
    if (candidate.routedM == null) continue;
    if (!best || candidate.routedM < (best.routedM as number)) best = candidate;
  }
  return best;
}

/** A routed point this close to the trail has reached it. */
export const DETOUR_MEETS_TRAIL_M = 15;

/** Squared equirectangular distance — cheap, and accurate at these short ranges. */
function approxSquaredMeters(a: number[], b: number[]): number {
  const toRad = Math.PI / 180;
  const x = (b[0] - a[0]) * Math.cos(((a[1] + b[1]) / 2) * toRad) * 6371000 * toRad;
  const y = (b[1] - a[1]) * 6371000 * toRad;
  return x * x + y * y;
}

/** Nearest trail vertex to `coord` within [minDist, maxDist], or null when none is within reach. */
function nearestTrailVertexWithin(
  trail: MeasuredLine,
  coord: number[],
  minDist: number,
  maxDist: number,
  reachM: number
): { index: number; distanceM: number } | null {
  const reachSq = reachM * reachM;
  let best: { index: number; distanceM: number } | null = null;
  let bestSq = Infinity;
  for (let i = 0; i < trail.coordinates.length; i++) {
    const along = trail.cumDist[i];
    if (along < minDist || along > maxDist) continue;
    const sq = approxSquaredMeters(coord, trail.coordinates[i]);
    if (sq <= reachSq && sq < bestSq) {
      bestSq = sq;
      best = { index: i, distanceM: along };
    }
  }
  return best;
}

/**
 * A routed way back to the trail (`detour`, running trail → drop point) often
 * reaches the trail well before the point it was routed to, then runs along
 * it — the routing engine only knows the nearest point as the crow flies.
 * Walk back from the drop point and cut the detour where it FIRST meets the
 * trail (within [minDist, maxDist]), so the detour is only the part off it
 * and the route rejoins there. Null when it never meets the trail before its
 * own start.
 */
export function cropDetourAtTrail(
  trail: MeasuredLine,
  detour: number[][],
  minDist: number,
  maxDist: number,
  meetsTrailM: number = DETOUR_MEETS_TRAIL_M
): { distanceM: number; point: number[]; coordinates: number[][] } | null {
  for (let i = detour.length - 1; i >= 0; i--) {
    const meet = nearestTrailVertexWithin(trail, detour[i], minDist, maxDist, meetsTrailM);
    if (!meet) continue;
    const point = trail.coordinates[meet.index];
    // Start exactly on the trail, so the slice and the detour join seamlessly.
    return { distanceM: meet.distanceM, point, coordinates: [point, ...detour.slice(i + 1)] };
  }
  return null;
}
